import styled from '@emotion/styled'
import {theme} from '../../features/Dashboard/theme'
import {PTThryvPeek} from '../../../../assets'

const THRYV_PEEK_URL = `url(${PTThryvPeek})`

/* ───────── Wrapper / TopBar ───────── */

export const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 900;
  background: linear-gradient(179.57deg, #171d37 0.38%, #6474a9 108.67%);
  left: 0;
  max-width: 100%;
  overflow-y: auto;
`

export const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`

export const PreviewTopBarWrap = styled.div`
  padding: 24px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 10px;
  }
`

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const TitleIconWrap = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const TitleTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const PageTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  font-family: ${theme.font.family};

  @media (max-width: 600px) {
    font-size: 15px;
  }
`

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-family: ${theme.font.family};
`

export const PreviewActionGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`

export const ExportPDFBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 13px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: ${theme.radius.full};
  color: #ffffff;
  cursor: pointer;
  flex-shrink: 0;
  transition: background ${theme.transition.fast};
  font-family: 'Open Sans';
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    gap: 6px;
    padding: 6px 10px;
  }
`

export const HidePreviewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 8px 13px;
  background: #d2daeb;
  border: none;
  border-radius: ${theme.radius.full};
  color: ${theme.colors.text};
  cursor: pointer;
  flex-shrink: 0;
  transition: background ${theme.transition.fast};
  font-family: 'Open Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px; /* 133.333% */
  &:hover {
    background: #c4cede;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 600px) {
    gap: 6px;
    padding: 6px 10px;
  }
`

/* ───────── Preview hero (rep-facing parity with client view) ───────── */

export const PreviewHero = styled.section`
  padding: 32px 40px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

export const PreviewHeroMeta = styled.p`
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  font-family: ${theme.font.family};
`

export const PreviewHeroTitle = styled.h2`
  margin: 0 auto 4px;
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  letter-spacing: -0.3px;
  font-family: ${theme.font.family};
  text-align: center;

  @media (max-width: 600px) {
    font-size: 18px;
  }
`

export const PreviewHeroDisclaimer = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  font-style: italic;
  color: rgba(255, 255, 255, 0.5);
  font-family: ${theme.font.family};
`

/* ───────── Cards layout ───────── */

export const CardsScrollArea = styled.div`
  position: relative;
`

export const ScrollArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => (props.dir === 'left' ? 'left: -16px;' : 'right: -16px;')}
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${theme.colors.border};
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${theme.colors.text};
  transition: background ${theme.transition.fast},
    opacity ${theme.transition.fast};
  opacity: ${props => (props.visible ? 1 : 0)};
  pointer-events: ${props => (props.visible ? 'auto' : 'none')};

  &:hover {
    background: ${theme.colors.hover};
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 900px) {
    display: none;
  }
`

export const CardsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  isolation: isolate;

  ${props =>
    props.scrollable
      ? `
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    justify-content: flex-start;
    padding-bottom: 8px;
    cursor: grab;
    @media (hover: none) { cursor: default; }

    &::-webkit-scrollbar { height: 5px; }
    &::-webkit-scrollbar-track { background: ${theme.colors.secondary}26; border-radius: 3px; }
    &::-webkit-scrollbar-thumb { background: #485b9d; border-radius: 3px; }
    &::-webkit-scrollbar-thumb:hover { background: #181e38; }
  `
      : `justify-content: center;`}

  @media (max-width: 900px) {
    flex-direction: column;
    overflow-x: unset;
    scroll-snap-type: none;
    cursor: default;
  }

  @media (max-width: 600px) {
    gap: 8px;
  }
`

const CARD_GRADIENT = 'linear-gradient(45deg, #80225F 0%, #5378FC 90.7%)'

export const CardWrapper = styled.div`
  background: ${props =>
    props.featured || props.clientSelected ? CARD_GRADIENT : '#ffffff'};
  display: flex;
  flex-direction: column;
  margin-top: ${props => (props.featured ? '-35px' : 0)};
  z-index: ${props => (props.featured ? 2 : 1)};
  padding: 10px;
  border-radius: 12px;
  border: 1px solid #bcbef6;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  position: relative;

  ${props =>
    props.scrollable
      ? `
    flex: 0 0 320px;
    width: 320px;
    scroll-snap-align: start;
  `
      : `
    flex: 0 0 calc((100% - 24px) / 3);
    max-width: calc((100% - 24px) / 3);
  `}

  @media (max-width: 900px) {
    flex: 1 1 auto;
    max-width: 100%;
    width: auto;
  }

  @media (max-width: 600px) {
    padding: 6px;
    border-radius: 10px;
  }

  /* The option the client chose — same gradient frame as a featured card, so it
     reads as the highlighted one among its neighbours. */
  ${props =>
    props.clientSelected &&
    `
    border-color: transparent;
    box-shadow: 0 6px 20px rgba(83, 120, 252, 0.3);
    z-index: 2;
  `}

  ${props =>
    props.client && props.featured
      ? `
    &::before {
      content: '';
      position: absolute;
      top: -75px;
      left: 50%;
      transform: translateX(-50%);
      width: 120px;
      height: 82px;
      background-image: ${THRYV_PEEK_URL};
      background-size: contain;
      background-repeat: no-repeat;
      background-position: bottom center;
      pointer-events: none;
      z-index: 10;
    }
  `
      : ''}
`

export const FeaturedBanner = styled.div`
  padding: 2px 0 5px;
  text-align: center;
  font-weight: 400;
  font-size: 14px;
  color: ${theme.colors.hover};
  font-family: ${theme.font.family};
`

export const CardBorderWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: 15px;
  background: #d2daeb;
`

export const OptionCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${props => (props.featured ? '0 0 15px 15px' : '15px')};
`

/* ───────── Accordion groups ───────── */

export const CardBody = styled.div`
  flex: 1;
`
export const GroupBody = styled.div`
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.1);
  padding: 8px;
  margin-top: 10px;
  border-radius: 10px;
  overflow-y: auto;
  height: 26rem;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: ${theme.colors.secondary}26;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #485b9d;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #181e38;
  }

  @media (max-width: 900px) {
    height: auto;
    max-height: none;
  }
`

export const GroupSection = styled.div`
  border-radius: 10px;
  overflow: hidden;
  margin: 5px 0;
  background: ${theme.colors.hover};

  &:last-child {
    margin-bottom: 0;
  }
`

export const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${theme.colors.border};

  &:hover {
    background: #ededf0;
  }
`

export const GroupArrow = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${theme.colors.muted};
`

export const GroupName = styled.span`
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
`

export const GroupTotal = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
  white-space: nowrap;
`

/* ───────── Product items ───────── */

export const ProductItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin: 3px 8px;
  border-radius: 12px;
  position: relative;
  border: 2px solid
    ${props =>
      props.highlighted ? theme.colors.secondary : theme.colors.surface};
  background: ${props => (props.highlighted ? '#fff8f5' : theme.colors.bg)};

  ${props =>
    props.highlighted &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 2px;
      height: 60%;
      background: #181e38;
      border-radius: 0 4px 4px 0;
    }
  `}

  @media (max-width: 600px) {
    gap: 8px;
    padding: 8px;
    margin: 3px 4px;
  }
`

export const ProductIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${props => props.bg || theme.colors.hover};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

export const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const ProductName = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: ${props => (props.highlighted ? 700 : 400)};
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ProductSubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
`

export const ProductPriceText = styled.span`
  font-size: 11px;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

export const ViewDetailsLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #4b5adb;
  cursor: pointer;
  font-family: ${theme.font.family};
  text-decoration: underline;

  &:hover {
    color: ${theme.colors.secondary};
  }
`

export const IncludedText = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

export const ProductTotal = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
  white-space: nowrap;
  flex-shrink: 0;
`

export const ProductDetails = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  margin: -2px 8px 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`

export const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`

export const DetailLabel = styled.span`
  flex: 0 0 90px;
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
  text-transform: capitalize;

  @media (max-width: 600px) {
    flex: 0 0 68px;
  }
`

export const DetailValue = styled.span`
  flex: 1;
  font-size: 11px;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
  word-break: break-all;
`

export const DetailDivider = styled.p`
  margin: 4px 0 2px;
  font-size: 10px;
  font-weight: 700;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-top: 1px solid ${theme.colors.border};
  padding-top: 6px;
`

export const AddonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  font-family: ${theme.font.family};
  color: ${theme.colors.text};
`

/* ───────── Coupons section ───────── */

export const CouponSection = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const CouponSectionTitle = styled.p`
  margin: 0 0 2px;
  font-size: 11px;
  font-weight: 700;
  font-family: ${theme.font.family};
  color: #166534;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const CouponRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

export const CouponTag = styled.span`
  font-size: 11px;
  font-weight: 700;
  font-family: ${theme.font.family};
  color: #166534;
  background: #dcfce7;
  border: 1px solid #86efac;
  border-radius: 4px;
  padding: 2px 7px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`

export const CouponDiscount = styled.span`
  font-size: 11px;
  font-weight: 600;
  font-family: ${theme.font.family};
  color: #16a34a;
  white-space: nowrap;
`

export const SavingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 5px;
  border-top: 1px solid #86efac;
  font-size: 12px;
  font-weight: 700;
  font-family: ${theme.font.family};
  color: #15803d;
`

/* ── Client-view select button (rendered inside ProposalCardTop via actionBtn) ── */

export const SelectBtnWrap = styled.div`
  padding: 0 20px 4px;
  background: #ffffff;
`

export const SelectBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  width: 100%;
  border-radius: ${theme.radius.full};
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-family: ${theme.font.family};
  transition: background ${theme.transition.fast},
    color ${theme.transition.fast}, transform 0.1s;
  background: #d2daeb;
  color: ${theme.colors.text};

  &:hover {
    background: #ff5000;
    color: #ffffff;
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus {
    outline: none;
  }
`
